// In-book search — wraps epub.js's book.search() across the whole spine.
//
// Returns hits with CFI + excerpt + chapter label so the UI can render a
// list and the user can jump to each result.

import type { Book as EpubBook } from "epubjs";

export interface SearchHit {
  cfi: string;
  excerpt: string;
  href: string;
  chapterLabel?: string;
}

/**
 * Search every spine item for `query`. epub.js's per-section search returns
 * raw match arrays; we flatten + bound results so we don't burn forever on
 * huge books.
 */
export async function searchBook(
  book: EpubBook,
  query: string,
  opts?: { maxResults?: number }
): Promise<SearchHit[]> {
  const max = opts?.maxResults ?? 200;
  const q = query.trim();
  if (q.length < 2) return [];
  const results: SearchHit[] = [];

  // epub.js v0.3 spine has .each(); each item has .load + .find
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const spine: any = (book as any).spine;
  if (!spine) return results;

  const items: unknown[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  spine.each((it: any) => items.push(it));

  for (const it of items) {
    if (results.length >= max) break;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const item: any = it;
    try {
      // load returns either a doc or a promise; either way we need the
      // section content to be parsed before find() works.
      await item.load(book.load.bind(book));
      const matches: { cfi: string; excerpt: string }[] = item.find(q) ?? [];
      for (const m of matches) {
        if (results.length >= max) break;
        results.push({
          cfi: m.cfi,
          excerpt: m.excerpt,
          href: item.href,
        });
      }
      item.unload?.();
    } catch (err) {
      // Skip un-searchable spine items (images, malformed XHTML).
      console.warn("[pagevoice] search skipped", item.href, err);
    }
  }

  return results;
}
